import React, { useEffect, useMemo, useState } from 'react';
import {
    Button,
    Table,
    Input,
    Breadcrumb,
    Tag,
    DatePicker,
    Row,
    Col,
    Grid,
    message,
} from 'antd';
import { Link } from 'react-router-dom';
import {
    FileExcelOutlined,
    FilePdfOutlined,
    DownloadOutlined,
    RightOutlined,
    SearchOutlined,
} from '@ant-design/icons';
import { promotionsServices } from '../../common/services';
import moment from 'moment';
import 'moment/locale/fr';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/** Debounce simple pour la recherche */
const useDebounce = (value, delay = 400) => {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const id = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(id);
    }, [value, delay]);
    return debounced;
};

const HistoriquePromotions = () => {
    const [data, setData] = useState([]);
    const [isLoading, setLoading] = useState(false);

    // 🔎 Filtres
    const [dateRange, setDateRange] = useState(null); // [dayjs/moment, dayjs/moment]
    const [query, setQuery] = useState('');           // nom/téléphone
    const queryDebounced = useDebounce(query, 400);

    // 📄 Pagination & tri (serveur)
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [total, setTotal] = useState(0);
    const [sorter, setSorter] = useState({ field: 'date', order: 'descend' }); // -date par défaut

    // ✅ breakpoints
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;           // < 576px
    const controlSize = isMobile ? 'small' : 'middle';

    // 📦 Params envoyés à l'API
    const apiParams = useMemo(() => {
        const params = {
            page,
            limit,
            // champ de tri serveur (accepte p.ex. "date", "-date", "user.firstName")
            sort: sorter?.field
                ? (sorter.order === 'descend' ? `-${Array.isArray(sorter.field) ? sorter.field.join('.') : sorter.field}`
                    : `${Array.isArray(sorter.field) ? sorter.field.join('.') : sorter.field}`)
                : '-date',
        };

        if (queryDebounced) params.q = queryDebounced.trim();
        if (dateRange && dateRange[0] && dateRange[1]) {
            params.start = dateRange[0].format('YYYY-MM-DD');
            params.end = dateRange[1].format('YYYY-MM-DD'); // le backend inclut 23:59:59.999
        }
        return params;
    }, [page, limit, sorter, queryDebounced, dateRange]);

    // 📥 Chargement serveur
    const load = async () => {
        try {
            setLoading(true);
            const res = await promotionsServices.listeDesChauffeursQuiOntActivePromoDimanche(apiParams);
            // res attendu: { items, total, totalPages, page, limit }
            setData(Array.isArray(res?.items) ? res.items : []);
            setTotal(res?.total || 0);

            if (res?.page && res.page !== page) setPage(res.page);
            if (res?.limit && res.limit !== limit) setLimit(res.limit);
        } catch (err) {
            console.error('Erreur API:', err);
            message.error("Impossible de charger l'historique des promotions.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [apiParams]);

    // ☎️ format CI
    const formatPhoneNumber = (phone) => {
        if (!phone) return '';
        let digits = String(phone).replace(/\D/g, '');
        if (digits.startsWith('225')) digits = digits.slice(3);
        if (digits.length === 9) digits = '0' + digits;
        return digits.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
    };

    // ⬇️ Exports (page courante)
    const exportRows = data.map((item) => ({
        Nom: `${item.user?.firstName || ''} ${item.user?.lastName || ''}`.trim(),
        Téléphone: formatPhoneNumber(item.user?.phone),
        Points: Number(item.user?.soldePoints?.point ?? 0),
        Date: moment(item.date).format('DD/MM/YYYY HH:mm:ss'),
    }));

    const exportToExcel = () => {
        const ws = XLSX.utils.json_to_sheet(exportRows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Promotions Dimanche');
        XLSX.writeFile(wb, 'promotions_dimanche.xlsx');
    };

    const exportToCSV = () => {
        const ws = XLSX.utils.json_to_sheet(exportRows);
        const csv = XLSX.utils.sheet_to_csv(ws);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        saveAs(blob, 'promotions_dimanche.csv');
    };

    const exportToPDF = () => {
        const doc = new jsPDF();
        doc.text('Promotions du Dimanche', 14, 10);
        autoTable(doc, {
            startY: 20,
            head: [['Nom', 'Téléphone', 'Points', 'Date']],
            body: data.map((item) => [
                `${item.user?.firstName || ''} ${item.user?.lastName || ''}`.trim(),
                formatPhoneNumber(item.user?.phone),
                String(Number(item.user?.soldePoints?.point ?? 0)),
                moment(item.date).format('DD/MM/YYYY HH:mm:ss'),
            ]),
        });
        doc.save('promotions_dimanche.pdf');
    };

    const columns = [
        {
            title: 'Nom',
            key: 'firstName',
            dataIndex: ['user', 'firstName'],       // sert aux événements de tri
            render: (_v, record) => `${record.user?.firstName || ''} ${record.user?.lastName || ''}`.trim(),
            sorter: true,                           // tri serveur possible via "user.firstName"
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Téléphone',
            key: 'phone',
            dataIndex: ['user', 'phone'],
            render: (v) => formatPhoneNumber(v),
            sorter: true,                           // tri serveur via "user.phone"
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Points',
            key: 'points',
            dataIndex: ['user', 'soldePoints', 'point'],
            render: (points) => {
                const n = Number(points ?? 0);
                const txt = n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                return <Tag color={n < 0 ? 'red' : 'green'}>{txt}</Tag>;
            },
            // (option) tu peux activer un tri serveur 'user.soldePoints.point' si tu le gères côté API
            // sorter: true,
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Date d’activation',
            dataIndex: 'date',
            key: 'date',
            render: (text) => moment(text).format('DD/MM/YYYY HH:mm:ss'),
            sorter: true,                           // tri serveur via "date"
            defaultSortOrder: 'descend',
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
    ];

    return (
        <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="row">
                    <div className="col-md-12">
                        <Breadcrumb
                            separator={<RightOutlined style={{ fontSize: 16, color: '#999', fontWeight: 'bolder' }} />}
                            style={{ marginBottom: 16 }}
                        >
                            <Breadcrumb.Item>
                                <Link to="/dashboard" style={{ fontSize: 16, fontWeight: 'bolder' }}>
                                    🏠 ACCUEIL
                                </Link>
                            </Breadcrumb.Item>
                            <Breadcrumb.Item>
                                <span style={{ fontSize: 16, fontWeight: 'bolder' }}>PROMOTION DIMANCHE</span>
                            </Breadcrumb.Item>
                        </Breadcrumb>

                        {/* 📋 TABLEAU */}
                        <div className="card mb-4">
                            <div className="card-body">
                                {/* 🔎 Filtres */}
                                <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                                    <Col xs={24} md={10}>
                                        <DatePicker.RangePicker
                                            size={controlSize}
                                            style={{ width: '100%' }}
                                            format="DD/MM/YYYY"
                                            value={dateRange}
                                            onChange={(val) => { setPage(1); setDateRange(val); setLoading(true); }}
                                            placeholder={['Date début', 'Date fin']}
                                        />
                                    </Col>
                                    <Col xs={24} md={14}>
                                        <Input
                                            size={controlSize}
                                            allowClear
                                            prefix={<SearchOutlined />}
                                            placeholder="Recherche (nom, téléphone)"
                                            value={query}
                                            onChange={(e) => { setPage(1); setQuery(e.target.value); setLoading(true); }}
                                        />
                                    </Col>
                                </Row>

                                {/* ⬇️ Exports */}
                                <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
                                    <Col xs={24} sm={12} md={8} lg={6} xl={6}>
                                        <Button block={isMobile} size={controlSize} icon={<FileExcelOutlined />} onClick={exportToExcel} type="primary">
                                            Exporter Excel (.xlsx)
                                        </Button>
                                    </Col>
                                    <Col xs={24} sm={12} md={8} lg={6} xl={6}>
                                        <Button block={isMobile} size={controlSize} icon={<DownloadOutlined />} onClick={exportToCSV}>
                                            Exporter CSV
                                        </Button>
                                    </Col>
                                    <Col xs={24} sm={12} md={8} lg={6} xl={6}>
                                        <Button block={isMobile} size={controlSize} icon={<FilePdfOutlined />} onClick={exportToPDF} danger>
                                            Exporter PDF
                                        </Button>
                                    </Col>
                                </Row>

                                <Table
                                    loading={isLoading ? { tip: 'Chargement des résultats…' } : false}
                                    size={isMobile ? 'small' : 'middle'}
                                    columns={columns}
                                    dataSource={data}
                                    rowKey={(record, idx) => record._id || `${record.contractorId}-${record.date}-${idx}`}
                                    pagination={{
                                        current: page,
                                        pageSize: limit,
                                        total,
                                        hideOnSinglePage: isMobile,
                                        showSizeChanger: !isMobile,
                                    }}
                                    onChange={(pagination, _filters, sorterArg) => {
                                        setLoading(true); // spinner immédiat
                                        setPage(pagination.current);
                                        setLimit(pagination.pageSize);
                                        const s = Array.isArray(sorterArg) ? sorterArg[0] : sorterArg;
                                        if (s && s.field) {
                                            setSorter({ field: s.field, order: s.order || 'descend' });
                                        } else {
                                            setSorter({ field: 'date', order: 'descend' });
                                        }
                                    }}
                                    scroll={{ x: true }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HistoriquePromotions;
